import React, { useEffect, useState } from 'react';

interface Stats {
  coffees: number;
  orders: { total: number; new: number };
  inquiries: { total: number; new: number };
  invoices: { total: number; draft: number; sent: number; overdue: number; paid: number };
}

interface Shipment {
  month: string;
  year: number;
  contracts: number;
  kg: number;
  usd: number;
  avgPrice: number;
}

export const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [shipments, setShipments] = useState<Shipment[]>([]);

  useEffect(() => {
    async function load() {
      const [coffeesRes, ordersRes, inquiriesRes, invoicesRes, contractsRes] = await Promise.all([
        fetch('/api/coffees').then(r => r.json()).catch(() => []),
        fetch('/api/sample-orders', { credentials: 'include' }).then(r => r.json()).catch(() => []),
        fetch('/api/inquiries', { credentials: 'include' }).then(r => r.json()).catch(() => []),
        fetch('/api/admin/invoices', { credentials: 'include' }).then(r => r.json()).catch(() => []),
        fetch('/api/admin/export-invoices?resource=contracts', { credentials: 'include' }).then(r => r.json()).catch(() => []),
      ]);

      setStats({
        coffees: Array.isArray(coffeesRes) ? coffeesRes.length : 0,
        orders: {
          total: Array.isArray(ordersRes) ? ordersRes.length : 0,
          new: Array.isArray(ordersRes) ? ordersRes.filter((o: any) => o.status === 'New').length : 0,
        },
        inquiries: {
          total: Array.isArray(inquiriesRes) ? inquiriesRes.length : 0,
          new: Array.isArray(inquiriesRes) ? inquiriesRes.filter((i: any) => i.status === 'New').length : 0,
        },
        invoices: {
          total: Array.isArray(invoicesRes) ? invoicesRes.length : 0,
          draft: Array.isArray(invoicesRes) ? invoicesRes.filter((i: any) => i.status === 'draft').length : 0,
          sent: Array.isArray(invoicesRes) ? invoicesRes.filter((i: any) => i.status === 'sent').length : 0,
          overdue: Array.isArray(invoicesRes) ? invoicesRes.filter((i: any) => i.status === 'sent' && i.dueDate && new Date(i.dueDate) < new Date()).length : 0,
          paid: Array.isArray(invoicesRes) ? invoicesRes.filter((i: any) => i.status === 'paid').length : 0,
        },
      });

      if (Array.isArray(contractsRes) && contractsRes.length > 0) {
        const map = new Map<string, Shipment>();
        for (const c of contractsRes) {
          const month = c.shippingMonth || 'Unknown';
          if (!map.has(month)) {
            const parts = month.split(' ');
            map.set(month, { month, year: parseInt(parts[parts.length - 1]) || 0, contracts: 0, kg: 0, usd: 0, avgPrice: 0 });
          }
          const s = map.get(month)!;
          s.contracts++;
          s.kg += parseFloat(c.totalQuantityKg) || 0;
          s.usd += parseFloat(c.totalValue) || 0;
        }
        const list = Array.from(map.values())
          .sort((a, b) => {
            const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
            const [aM, , aY] = a.month.split(' ');
            const [bM, , bY] = b.month.split(' ');
            const yearDiff = parseInt(aY) - parseInt(bY);
            return yearDiff !== 0 ? yearDiff : months.indexOf(aM) - months.indexOf(bM);
          })
          .map(s => ({ ...s, avgPrice: s.kg > 0 ? s.usd / s.kg : 0 }));
        setShipments(list);
      }
    }
    load();
  }, []);

  const fmtUSD = (n: number) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const fmtKg = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' kg';

  const totalRevenue = shipments.reduce((s, x) => s + x.usd, 0);
  const totalKg = shipments.reduce((s, x) => s + x.kg, 0);
  const totalContracts = shipments.reduce((s, x) => s + x.contracts, 0);
  const maxUSD = Math.max(...shipments.map(s => s.usd), 1);

  const byYear = shipments.reduce((acc: Record<number, { usd: number; kg: number }>, s) => {
    if (!acc[s.year]) acc[s.year] = { usd: 0, kg: 0 };
    acc[s.year].usd += s.usd;
    acc[s.year].kg += s.kg;
    return acc;
  }, {});
  const years = Object.keys(byYear).map(Number).sort();

  if (!stats) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-40 bg-white rounded-lg animate-pulse" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-28 bg-white rounded-2xl animate-pulse shadow-sm" />
          ))}
        </div>
      </div>
    );
  }

  const cards = [
    {
      label: 'Coffees', value: stats.coffees, sub: 'products listed',
      icon: <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 8h11v5.5a4.5 4.5 0 01-4.5 4.5h-2A4.5 4.5 0 013 13.5V8z"/><path d="M14 9h1.5a2.5 2.5 0 010 5H14"/></svg>,
      color: 'from-amber-500/10 to-orange-500/5',
    },
    {
      label: 'Orders', value: stats.orders.total, sub: `${stats.orders.new} new`, highlight: stats.orders.new > 0,
      icon: <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 5l2-2h10l2 2"/><rect x="3" y="5" width="14" height="12" rx="1"/><path d="M7 9a3 3 0 006 0"/></svg>,
      color: 'from-blue-500/10 to-indigo-500/5',
    },
    {
      label: 'Inquiries', value: stats.inquiries.total, sub: `${stats.inquiries.new} new`, highlight: stats.inquiries.new > 0,
      icon: <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 4h12v9H7l-3 3V4z"/></svg>,
      color: 'from-violet-500/10 to-purple-500/5',
    },
    {
      label: 'Invoices', value: stats.invoices.total, sub: `${stats.invoices.draft} draft · ${stats.invoices.sent} sent · ${stats.invoices.paid} paid`,
      icon: <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M5 3h7l4 4v10a1 1 0 01-1 1H5a1 1 0 01-1-1V4a1 1 0 011-1z"/><path d="M12 3v4h4"/></svg>,
      color: 'from-emerald-500/10 to-teal-500/5',
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] tracking-tight">Dashboard</h1>
          <p className="text-sm text-[#A3A3A3] mt-1">Overview of your coffee business</p>
        </div>
      </div>

      {/* Top stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        {cards.map(card => (
          <div key={card.label} className="bg-white rounded-2xl border border-[#E8E6E2]/60 shadow-sm shadow-black/[0.03] p-5 hover:shadow-md hover:shadow-black/[0.05] transition-shadow">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">{card.label}</p>
                <p className="text-3xl font-bold text-[#1A1A1A] mt-1.5 tabular-nums">{card.value}</p>
              </div>
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center text-[#6B6B6B]`}>
                {card.icon}
              </div>
            </div>
            <p className={`text-[13px] mt-2 ${card.highlight ? 'text-[#F05A1E] font-semibold' : 'text-[#A3A3A3]'}`}>
              {card.sub}
            </p>
          </div>
        ))}
      </div>

      {stats.invoices.overdue > 0 && (
        <div className="bg-amber-50 border border-amber-200/60 rounded-xl p-4 text-amber-800 text-sm font-medium mb-8 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="text-amber-600">
              <path d="M8 6v3M8 11.5v.5"/><path d="M3.5 14h9L8 3 3.5 14z"/>
            </svg>
          </div>
          {stats.invoices.overdue} invoice{stats.invoices.overdue > 1 ? 's' : ''} overdue — review and follow up
        </div>
      )}

      {/* Revenue Analytics */}
      {shipments.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-[#1A1A1A] tracking-tight">Revenue Analytics</h2>
            <span className="text-[11px] text-[#A3A3A3] uppercase tracking-wider font-medium bg-[#F5F3F0] px-3 py-1 rounded-full">All figures FOB · USD</span>
          </div>

          {/* Summary KPI row */}
          <div className="grid grid-cols-3 gap-5 mb-6">
            <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-5 shadow-sm shadow-black/[0.03]">
              <p className="text-[11px] uppercase tracking-[0.12em] text-[#A3A3A3] font-semibold">Total Revenue</p>
              <p className="text-2xl font-bold text-[#1A1A1A] mt-1.5 tabular-nums">{fmtUSD(totalRevenue)}</p>
              <p className="text-[13px] text-[#A3A3A3] mt-1">{shipments.length} shipments · {totalContracts} contracts</p>
            </div>
            <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-5 shadow-sm shadow-black/[0.03]">
              <p className="text-[11px] uppercase tracking-[0.12em] text-[#A3A3A3] font-semibold">Total Volume</p>
              <p className="text-2xl font-bold text-[#1A1A1A] mt-1.5 tabular-nums">{fmtKg(totalKg)}</p>
              <p className="text-[13px] text-[#A3A3A3] mt-1">Avg {fmtUSD(totalKg > 0 ? totalRevenue / totalKg : 0)}/kg overall</p>
            </div>
            <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-5 shadow-sm shadow-black/[0.03]">
              <p className="text-[11px] uppercase tracking-[0.12em] text-[#A3A3A3] font-semibold">Year on Year</p>
              {years.length >= 2 ? (() => {
                const latest = byYear[years[years.length - 1]].usd;
                const prev = byYear[years[years.length - 2]].usd;
                const pct = prev > 0 ? ((latest - prev) / prev * 100) : 0;
                const up = pct >= 0;
                return (
                  <>
                    <div className="flex items-center gap-2 mt-1.5">
                      <p className={`text-2xl font-bold tabular-nums ${up ? 'text-emerald-600' : 'text-red-600'}`}>
                        {up ? '+' : ''}{pct.toFixed(0)}%
                      </p>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${up ? 'bg-emerald-100' : 'bg-red-100'}`}>
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={up ? 'text-emerald-600' : 'text-red-600 rotate-180'}>
                          <path d="M6 9V3M3 5l3-3 3 3"/>
                        </svg>
                      </div>
                    </div>
                    <p className="text-[13px] text-[#A3A3A3] mt-1">{years[years.length - 2]} → {years[years.length - 1]}</p>
                  </>
                );
              })() : (
                <p className="text-2xl font-bold text-[#C4C4C4] mt-1.5">—</p>
              )}
            </div>
          </div>

          {/* Shipment breakdown + bar chart */}
          <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl overflow-hidden mb-6 shadow-sm shadow-black/[0.03]">
            <div className="px-6 py-4 border-b border-[#E8E6E2]/60">
              <h3 className="text-sm font-semibold text-[#1A1A1A]">Revenue by Shipment</h3>
            </div>
            <div className="divide-y divide-[#E8E6E2]/60">
              {shipments.map(s => {
                const barPct = (s.usd / maxUSD) * 100;
                return (
                  <div key={s.month} className="px-6 py-4 hover:bg-[#FAFAF8]/50 transition-colors">
                    <div className="flex items-center justify-between mb-2.5">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-[#1A1A1A] w-36">{s.month}</span>
                        <span className="text-xs text-[#A3A3A3]">{s.contracts} contracts · {fmtKg(s.kg)}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-bold text-[#1A1A1A] tabular-nums">{fmtUSD(s.usd)}</span>
                        <span className="text-xs text-[#A3A3A3] ml-2">${s.avgPrice.toFixed(2)}/kg</span>
                      </div>
                    </div>
                    <div className="h-2 bg-[#F5F3F0] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#1A1A1A] to-[#404040] rounded-full transition-all duration-700 ease-out"
                        style={{ width: `${barPct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Year-on-year table */}
          {years.length > 0 && (
            <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl overflow-hidden shadow-sm shadow-black/[0.03]">
              <div className="px-6 py-4 border-b border-[#E8E6E2]/60">
                <h3 className="text-sm font-semibold text-[#1A1A1A]">Annual Summary</h3>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E8E6E2]/60 bg-[#FAFAF8]/50">
                    <th className="px-6 py-3 text-left text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Year</th>
                    <th className="px-6 py-3 text-right text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Revenue</th>
                    <th className="px-6 py-3 text-right text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Volume</th>
                    <th className="px-6 py-3 text-right text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Avg $/kg</th>
                    <th className="px-6 py-3 text-right text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">vs Prior</th>
                  </tr>
                </thead>
                <tbody>
                  {years.map((yr, i) => {
                    const d = byYear[yr];
                    const prev = i > 0 ? byYear[years[i - 1]] : null;
                    const pct = prev && prev.usd > 0 ? ((d.usd - prev.usd) / prev.usd * 100) : null;
                    return (
                      <tr key={yr} className="border-b border-[#E8E6E2]/60 last:border-0 hover:bg-[#FAFAF8]/50 transition-colors">
                        <td className="px-6 py-4 font-semibold text-[#1A1A1A]">{yr}</td>
                        <td className="px-6 py-4 text-right font-bold text-[#1A1A1A] tabular-nums">{fmtUSD(d.usd)}</td>
                        <td className="px-6 py-4 text-right text-[#6B6B6B] tabular-nums">{fmtKg(d.kg)}</td>
                        <td className="px-6 py-4 text-right text-[#6B6B6B] tabular-nums">${(d.kg > 0 ? d.usd / d.kg : 0).toFixed(2)}</td>
                        <td className="px-6 py-4 text-right tabular-nums">
                          {pct !== null ? (
                            <span className={`inline-flex items-center gap-1 font-semibold ${pct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {pct >= 0 ? '+' : ''}{pct.toFixed(0)}%
                            </span>
                          ) : (
                            <span className="text-[#C4C4C4]">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};
